import { noop } from 'lodash';

import { Flex } from '@project44/manifest-ds';
import { OrderIdentifierReference } from '@project44-models/shipment-entity';

import { EntityEnum } from '../../definitions/enums/Entity';
import { ItemTypesEnum } from '../../definitions/enums/ItemType';
import { ChildrenType } from '../../definitions/types/Children';
import { Attributes, Identifiers } from '../../definitions/types/shipment';
import { CardReference } from '../CardReference';
import { Collapsible } from '../Collapsible';
import { Panel } from '../Panel';
import { LocationTypeEnum } from '../RelatedReferenceItem';
import {
  getMapperFn,
  MAX_COLLAPSED_REFERENCES,
  sectionsTitles,
} from './getItemsSection';
import { CopyHandlerOutput } from './utils';

import { collapsiblePanel, ScrollPanel } from './styles';

interface RenderProps {
  data: Identifiers[] | Attributes[] | OrderIdentifierReference[];
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapperFn: ((a: any) => JSX.Element) | null;
  hasFooter: boolean;
}

const render = ({
  data,
  mapperFn,
  hasFooter,
}: RenderProps): ChildrenType | null => {
  if (!data.length) return null;
  return (hasFooter ? data.slice(0, MAX_COLLAPSED_REFERENCES) : data).map(
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapperFn as any,
  );
};

interface ItemSectionProps {
  itemType: ItemTypesEnum;
  data: Identifiers[] | Attributes[] | OrderIdentifierReference[];
  copyHandler: CopyHandlerOutput;
  renderType: 'card' | 'collapsible';
  onExpand?: () => void;
  onClose?: () => void;
  hasFooter?: boolean;
  expanded?: boolean;
  isPubliclyShared?: boolean;
  masterShipmentId?: string;
  locationType?: LocationTypeEnum;
}

export const ItemSection = (props: ItemSectionProps) => {
  const {
    itemType,
    data,
    copyHandler,
    hasFooter,
    renderType,
    expanded,
    onClose,
    onExpand,
  } = props;
  const dataLength = data.length;

  const extra = (
    <CardReference.CopyButton
      disabled={!dataLength}
      onClick={copyHandler.copyAllItems}
    />
  );

  const displaySeeAll = data.length > MAX_COLLAPSED_REFERENCES;

  const footer = displaySeeAll ? (
    <Flex justify="between" align="center">
      <CardReference.SeeMore count={data.length - MAX_COLLAPSED_REFERENCES} />
      <CardReference.SeeAllButton
        disabled={expanded}
        onClick={() => {
          if (onExpand) onExpand();
        }}
      />
    </Flex>
  ) : undefined;

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapperFn = getMapperFn({ ...props, locationType: 'default' } as any);
  const expandedViewMapperFn = getMapperFn({
    ...props,
    locationType: 'expanded',
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

  if (renderType === 'card') {
    return (
      <CardReference
        title={sectionsTitles[itemType]}
        extra={extra}
        footer={hasFooter ? footer : undefined}
        dataLength={dataLength}
      >
        {render({ data, mapperFn, hasFooter: !!hasFooter }) as ChildrenType}
      </CardReference>
    );
  }

  if (renderType === 'collapsible') {
    if (data.length <= MAX_COLLAPSED_REFERENCES) return null;

    return (
      <Collapsible active={expanded}>
        <Panel
          sx={collapsiblePanel}
          title={sectionsTitles[itemType]}
          panelItemsCount={data.length}
          entityType={EntityEnum.shipment}
          onClose={onClose ? onClose : noop}
          closable={!!onClose}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <ScrollPanel>{data.map(expandedViewMapperFn as any)}</ScrollPanel>
        </Panel>
      </Collapsible>
    );
  }

  return null;
};
